import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import CartDrawer from '../home/components/CartDrawer';
import { useSEO } from '../../hooks/useSEO';

export default function TermsPage() {
  const [activeSection, setActiveSection] = useState('overview');
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://motionboyz.org';

  useSEO({
    title: 'Terms of Service | Motion Boyz – Legal Terms of Use',
    description: 'Read Motion Boyz Terms of Service. Learn about our policies on website use, orders, payments, shipping, returns, and intellectual property rights.',
    canonical: '/terms',
    ogImage: `${siteUrl}/og-image.jpg`,
    ogType: 'article',
  });

  useEffect(() => {
    const sections = [
      'overview',
      'online-store-terms',
      'products-pricing',
      'orders-payment',
      'shipping',
      'returns-refunds',
      'intellectual-property',
      'limitation-liability',
      'indemnification',
      'changes-terms',
      'governing-law',
      'contact',
    ];

    const handleScroll = () => {
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top >= 0 && rect.top <= 300) {
            setActiveSection(id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 100;
      const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const sections = [
    { id: 'overview', label: '1. Overview' },
    { id: 'online-store-terms', label: '2. Online Store Terms' },
    { id: 'products-pricing', label: '3. Products & Pricing' },
    { id: 'orders-payment', label: '4. Orders & Payment' },
    { id: 'shipping', label: '5. Shipping' },
    { id: 'returns-refunds', label: '6. Returns & Refunds' },
    { id: 'intellectual-property', label: '7. Intellectual Property' },
    { id: 'limitation-liability', label: '8. Limitation of Liability' },
    { id: 'indemnification', label: '9. Indemnification' },
    { id: 'changes-terms', label: '10. Changes to Terms' },
    { id: 'governing-law', label: '11. Governing Law' },
    { id: 'contact', label: '12. Contact Information' },
  ];

  return (
    <div className="bg-black min-h-screen">
      <Navbar />
      <CartDrawer />

      {/* Hero Section */}
      <div className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://public.readdy.ai/ai/img_res/58edf040d576283f95ca6ae7d9df1926.jpg)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black"></div>

        <div className="relative z-10 text-center px-6 max-w-4xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00d4ff]/30 bg-[#00d4ff]/5 mb-6">
            <i className="ri-file-text-line text-[#00d4ff] text-sm"></i>
            <span className="text-[#00d4ff] text-xs tracking-widest uppercase font-semibold">Legal</span>
          </div>
          <h1
            className="text-5xl md:text-6xl font-bold text-white mb-4 tracking-tight"
            style={{ textShadow: '0 0 30px rgba(0,212,255,0.3)' }}
          >
            Terms of Service
          </h1>
          <p className="text-gray-400 text-base tracking-wide max-w-2xl mx-auto">
            Please read these terms carefully before using our website and services
          </p>
          <p className="text-[#00d4ff] text-sm mt-4 tracking-wider">
            Effective Date: <span className="font-semibold">February 27, 2026</span>
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-[90%] max-w-[1400px] mx-auto py-16 lg:py-24">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sidebar Navigation - Desktop */}
          <aside className="hidden lg:block lg:w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-1">
              <p className="text-gray-500 text-xs tracking-widest uppercase font-semibold mb-4 px-4">
                Quick Navigation
              </p>
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full text-left px-4 py-2.5 text-sm tracking-wide rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeSection === section.id
                      ? 'bg-[#00d4ff]/10 text-[#00d4ff] border-l-2 border-[#00d4ff]'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </aside>

          {/* Content */}
          <div className="flex-1 max-w-4xl">

            {/* Business Info Banner */}
            <div className="mb-12 p-5 bg-[#111] border border-[#00d4ff]/10 rounded-xl flex flex-wrap gap-6 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <i className="ri-building-line text-[#00d4ff]"></i>
                <span><span className="text-white font-semibold">Business:</span> Motion Boyz</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-global-line text-[#00d4ff]"></i>
                <span><span className="text-white font-semibold">Website:</span> motionboyz.org</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line text-[#00d4ff]"></i>
                <span><span className="text-white font-semibold">Effective:</span> February 27, 2026</span>
              </div>
            </div>

            {/* 1. Overview */}
            <section id="overview" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full"></div>
                <h2 className="text-3xl font-bold text-white tracking-tight">1. Overview</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  This website is operated by Motion Boyz. Throughout the site, the terms "we," "us," and "our" refer to
                  Motion Boyz. By visiting our site and/or purchasing something from us, you engage in our "Service" and
                  agree to be bound by the following Terms of Service.
                </p>
                <p>
                  If you do not agree to all the terms and conditions of this agreement, you may not access the website or use any services.
                </p>
              </div>
            </section>

            {/* 2. Online Store Terms */}
            <section id="online-store-terms" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full"></div>
                <h2 className="text-3xl font-bold text-white tracking-tight">2. Online Store Terms</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>By agreeing to these Terms of Service, you represent that:</p>
                <ul className="list-disc list-inside space-y-2 pl-4">
                  <li>You are at least the age of majority in your state or province of residence</li>
                  <li>You will not use our products for any illegal or unauthorized purpose</li>
                  <li>You will not violate any laws in your jurisdiction</li>
                </ul>
                <p>A breach of any of the Terms will result in immediate termination of your Services.</p>
              </div>
            </section>

            {/* 3. Products & Pricing */}
            <section id="products-pricing" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full"></div>
                <h2 className="text-3xl font-bold text-white tracking-tight">3. Products &amp; Pricing</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <ul className="list-disc list-inside space-y-2 pl-4">
                  <li>All products are subject to availability.</li>
                  <li>We reserve the right to limit quantities of any product.</li>
                  <li>Prices are subject to change without notice.</li>
                  <li>We reserve the right to discontinue any product at any time.</li>
                </ul>
                <p>
                  We make every effort to display product colors and images accurately, but we cannot guarantee your
                  device will display colors exactly.
                </p>
              </div>
            </section>

            {/* 4. Orders & Payment */}
            <section id="orders-payment" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full"></div>
                <h2 className="text-3xl font-bold text-white tracking-tight">4. Orders &amp; Payment</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  We reserve the right to refuse or cancel any order for any reason, including:
                </p>
                <ul className="list-disc list-inside space-y-2 pl-4">
                  <li>Suspected fraud</li>
                  <li>Incorrect pricing</li>
                  <li>Product availability errors</li>
                </ul>
                <p>You agree to provide current, complete, and accurate purchase information.</p>
              </div>
            </section>

            {/* 5. Shipping */}
            <section id="shipping" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full"></div>
                <h2 className="text-3xl font-bold text-white tracking-tight">5. Shipping</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  Shipping times provided are estimates and not guaranteed. Motion Boyz is not responsible for:
                </p>
                <ul className="list-disc list-inside space-y-2 pl-4">
                  <li>Carrier delays</li>
                  <li>Lost or stolen packages once marked delivered</li>
                  <li>Incorrect addresses provided by the customer</li>
                </ul>
                <p>Customers are responsible for entering the correct shipping information.</p>
              </div>
            </section>

            {/* 6. Returns & Refunds */}
            <section id="returns-refunds" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full"></div>
                <h2 className="text-3xl font-bold text-white tracking-tight">6. Returns &amp; Refunds</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  Please refer to our{' '}
                  <a href="/refund" className="text-[#00d4ff] hover:underline cursor-pointer">
                    Return &amp; Refund Policy
                  </a>{' '}
                  for full details.
                </p>
                <p>
                  If items are returned without meeting our return requirements, we reserve the right to deny a refund.
                </p>
              </div>
            </section>

            {/* 7. Intellectual Property */}
            <section id="intellectual-property" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full"></div>
                <h2 className="text-3xl font-bold text-white tracking-tight">7. Intellectual Property</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>All content on this website, including:</p>
                <ul className="list-disc list-inside space-y-2 pl-4">
                  <li>Logos</li>
                  <li>Graphics</li>
                  <li>Designs</li>
                  <li>Images</li>
                  <li>Product artwork</li>
                  <li>Branding elements</li>
                </ul>
                <p>
                  Are the exclusive property of Motion Boyz and may not be copied, reproduced, or distributed without written permission.
                </p>
                <p>Unauthorized use of our designs may result in legal action.</p>
              </div>
            </section>

            {/* 8. Limitation of Liability */}
            <section id="limitation-liability" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full"></div>
                <h2 className="text-3xl font-bold text-white tracking-tight">8. Limitation of Liability</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>Motion Boyz shall not be liable for any:</p>
                <ul className="list-disc list-inside space-y-2 pl-4">
                  <li>Direct</li>
                  <li>Indirect</li>
                  <li>Incidental</li>
                  <li>Consequential damages</li>
                </ul>
                <p>Resulting from the use of our products or website.</p>
                <p>Use of our site and products is at your own risk.</p>
              </div>
            </section>

            {/* 9. Indemnification */}
            <section id="indemnification" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full"></div>
                <h2 className="text-3xl font-bold text-white tracking-tight">9. Indemnification</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  You agree to indemnify and hold Motion Boyz harmless from any claim or demand due to your breach of
                  these Terms or violation of any law.
                </p>
              </div>
            </section>

            {/* 10. Changes to Terms */}
            <section id="changes-terms" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full"></div>
                <h2 className="text-3xl font-bold text-white tracking-tight">10. Changes to Terms</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  We reserve the right to update, change, or replace any part of these Terms of Service at any time.
                </p>
                <p>
                  It is your responsibility to check this page periodically for changes.
                </p>
              </div>
            </section>

            {/* 11. Governing Law */}
            <section id="governing-law" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full"></div>
                <h2 className="text-3xl font-bold text-white tracking-tight">11. Governing Law</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of the United States and
                  the State of [Insert Your State].
                </p>
              </div>
            </section>

            {/* 12. Contact Information */}
            <section id="contact" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full"></div>
                <h2 className="text-3xl font-bold text-white tracking-tight">12. Contact Information</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>Questions about the Terms of Service should be sent to:</p>
                <div className="bg-[#111] border border-[#00d4ff]/10 rounded-xl p-6 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#00d4ff]/10 text-[#00d4ff]">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Email</p>
                      <a
                        href="mailto:llc.motionboyz@gmail.com"
                        className="text-[#00d4ff] hover:underline cursor-pointer text-sm"
                      >
                        llc.motionboyz@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#00d4ff]/10 text-[#00d4ff]">
                      <i className="ri-building-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Business Name</p>
                      <p className="text-gray-400 text-sm">Motion Boyz</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Bottom CTA */}
            <div className="mt-16 p-8 bg-gradient-to-br from-[#00d4ff]/5 to-transparent border border-[#00d4ff]/20 rounded-2xl text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#00d4ff]/10 text-[#00d4ff] mx-auto mb-4">
                <i className="ri-checkbox-circle-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Ready to Shop?</h3>
              <p className="text-gray-400 mb-6 max-w-md mx-auto">
                Explore our exclusive collection of luxury streetwear designed for those who never stop moving.
              </p>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/shop')}
                className="px-8 py-3 bg-[#00d4ff] text-black font-bold text-sm tracking-widest uppercase rounded-full hover:bg-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
                style={{ boxShadow: '0 0 30px rgba(0,212,255,0.4)' }}
              >
                Shop Now
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}